import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from '../../hooks/useTranslation';
import styles from './ContactForm.module.css';

const ContactForm = () => {
  const recaptchaRef = useRef(null);
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });

  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: false,
    message: ''
  });

  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate reCAPTCHA
    if (!recaptchaToken) {
      setStatus({
        loading: false,
        success: false,
        error: true,
        message: t('contact.messages.recaptchaRequired')
      });
      return;
    }

    setStatus({ loading: true, success: false, error: false, message: '' });

    try {
      // EmailJS Configuration - Get these from your EmailJS dashboard
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID';
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID';
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';

      // Template parameters matching your EmailJS template variables
      const templateParams = {
        from_name: `${formData.firstName} ${formData.lastName}`.trim(),
        name: `${formData.firstName} ${formData.lastName}`.trim(), // Also send as 'name' for template
        from_email: formData.email,
        email: formData.email, // Send both 'from_email' and 'email' for compatibility
        subject: formData.subject,
        from_message: formData.message
        // Note: {{time}} is auto-generated by EmailJS, no need to send it
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      setStatus({
        loading: false,
        success: true,
        error: false,
        message: t('contact.messages.success')
      });

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        message: ''
      });

      // Reset reCAPTCHA
      setRecaptchaToken(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

    } catch (error) {
      console.error('EmailJS Error:', error);
      setStatus({
        loading: false,
        success: false,
        error: true,
        message: t('contact.messages.error')
      });
    }
  };

  return (
    <div className={styles.formWrapper}>
      <h2 className={styles.title}>{t('contact.title')}</h2>
      <p className={styles.subtitle}>
        {t('contact.subtitle')}
      </p>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.nameFields}>
          <div className={styles.field}>
            <label htmlFor="firstName" className={styles.label}>
              {t('contact.form.firstName')} <span className={styles.required}>{t('contact.form.required')}</span>
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className={styles.input}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="lastName" className={styles.label}>
              {t('contact.form.lastName')} <span className={styles.required}>{t('contact.form.required')}</span>
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              className={styles.input}
            />
          </div>
        </div>

        <div className={styles.field}>
          <label htmlFor="email" className={styles.label}>
            {t('contact.form.email')} <span className={styles.required}>{t('contact.form.required')}</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className={styles.input}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="subject" className={styles.label}>
            {t('contact.form.subject')} <span className={styles.required}>{t('contact.form.required')}</span>
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
            className={styles.input}
            placeholder={t('contact.form.subjectPlaceholder')}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="message" className={styles.label}>
            {t('contact.form.message')} <span className={styles.required}>{t('contact.form.required')}</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows="6"
            className={styles.textarea}
          />
        </div>

        <div className={styles.recaptchaWrapper}>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
            onChange={handleRecaptchaChange}
            onExpired={() => setRecaptchaToken(null)}
          />
        </div>

        {status.message && (
          <div className={`${styles.message} ${status.success ? styles.success : styles.error}`}>
            {status.message}
          </div>
        )}

        <button
          type="submit"
          disabled={status.loading || !recaptchaToken}
          className={styles.submitButton}
        >
          {status.loading ? t('contact.form.sending') : t('contact.form.submit')}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
